/* dgs-groomsman-designer.js
 * -------------------------
 * Wedding-party monogram disc designer for /products/groomsman-custom-disc-golf-disc.
 *
 * What it does
 *   - Draws a live preview (topo-contour background + monogram initial + title/name/date)
 *     from the fields rendered by snippets/dgs-groomsman-designer.liquid. An
 *     optional second initial turns the monogram into a couple's "J & L" lockup
 *     at half size (added 2026-09-06 after a custom order).
 *   - Renders the SAME drawing at print size (2400px = 8" @ 300 DPI, matching the
 *     disc customizer's PRINT_MAX_EDGE) to a JPEG and attaches it to the product
 *     form as a file input named properties[_DGS Design URL].
 *   - cart-drawer.js submits the form with new FormData(form), so the JPEG is
 *     posted multipart to /cart/add.js; Shopify stores it on its CDN and the
 *     order property becomes the file URL — the same property the print-batch
 *     tool downloads, so these orders flow into the batch zip + sheet with no
 *     workflow changes.
 *
 * The print file is generated on the buyer's device and uploaded once, so the
 * preview and the print file always come from the same code path.
 */
(function () {
  'use strict';

  var root = document.getElementById('dgs-gm-designer');
  if (!root) return;

  var form = root.closest('form');
  if (!form) return;

  // ---------- element refs ----------
  var els = {
    role: document.getElementById('dgs-gm-role'),
    customRole: document.getElementById('dgs-gm-role-custom'),
    customRoleWrap: document.getElementById('dgs-gm-role-custom-wrap'),
    name: document.getElementById('dgs-gm-name'),
    initial: document.getElementById('dgs-gm-initial'),
    initial2: document.getElementById('dgs-gm-initial2'),
    date: document.getElementById('dgs-gm-date'),
    canvas: document.getElementById('dgs-gm-preview'),
    file: document.getElementById('dgs-gm-file'),
    status: document.getElementById('dgs-gm-status')
  };

  // ---------- deterministic topo-contour background ----------
  // Arithmetic-only value noise (no Math.sin) + marching squares + Chaikin
  // smoothing. ~4KB of code instead of shipping ~600KB of baked path data.
  function hash2(ix, iy, seed) {
    var h = ix * 374761393 + iy * 668265263 + seed * 2147483647;
    h = (h ^ (h >>> 13)) >>> 0;
    h = (h * 1274126177) >>> 0;
    h = (h ^ (h >>> 16)) >>> 0;
    return h / 4294967295;
  }
  function sstep(t) { return t * t * (3 - 2 * t); }
  function vnoise(x, y, seed) {
    var ix = Math.floor(x), iy = Math.floor(y);
    var fx = x - ix, fy = y - iy;
    var a = hash2(ix, iy, seed), b = hash2(ix + 1, iy, seed);
    var c = hash2(ix, iy + 1, seed), d = hash2(ix + 1, iy + 1, seed);
    var ux = sstep(fx), uy = sstep(fy);
    return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
  }
  function fbm(x, y, seed) {
    var v = 0, amp = 0.62, freq = 1;
    for (var o = 0; o < 3; o++) {
      v += amp * vnoise(x * freq, y * freq, seed + o * 101);
      amp *= 0.42; freq *= 2.1;
    }
    return v;
  }
  function field(nx, ny) {
    var wx = fbm(nx * 0.9 + 7.3, ny * 0.9 + 2.9, 40) * 1.6;
    var wy = fbm(nx * 0.9 + 91.2, ny * 0.9 + 55.7, 41) * 1.6;
    return fbm(nx * 1.1 + wx, ny * 1.1 + wy, 42);
  }

  var ART = 2400;          // master art space; all layout below is in these units
  var topoLines = null;    // [[ [x,y], ... ], ...] in ART space

  function buildTopo() {
    if (topoLines) return topoLines;
    var N = 150, step = ART / N;
    var grid = [], mn = 1e9, mx = -1e9, i, j;
    for (j = 0; j <= N; j++) {
      var row = [];
      for (i = 0; i <= N; i++) {
        var v = field(i / N * 3.0, j / N * 3.0);
        row.push(v);
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
      grid.push(row);
    }
    function contoursAt(t) {
      var segs = [];
      var lerp = function (va, vb) { return (t - va) / (vb - va); };
      for (var j = 0; j < N; j++) {
        for (var i = 0; i < N; i++) {
          var v0 = grid[j][i], v1 = grid[j][i + 1], v2 = grid[j + 1][i + 1], v3 = grid[j + 1][i];
          var idx = 0;
          if (v0 > t) idx |= 1;
          if (v1 > t) idx |= 2;
          if (v2 > t) idx |= 4;
          if (v3 > t) idx |= 8;
          if (idx === 0 || idx === 15) continue;
          var x = i * step, y = j * step;
          var top = [x + step * lerp(v0, v1), y];
          var right = [x + step, y + step * lerp(v1, v2)];
          var bottom = [x + step * lerp(v3, v2), y + step];
          var left = [x, y + step * lerp(v0, v3)];
          switch (idx) {
            case 1: case 14: segs.push([left, top]); break;
            case 2: case 13: segs.push([top, right]); break;
            case 3: case 12: segs.push([left, right]); break;
            case 4: case 11: segs.push([right, bottom]); break;
            case 6: case 9: segs.push([top, bottom]); break;
            case 7: case 8: segs.push([left, bottom]); break;
            case 5: segs.push([left, top]); segs.push([right, bottom]); break;
            case 10: segs.push([top, right]); segs.push([left, bottom]); break;
          }
        }
      }
      // chain segments into polylines; endpoints can meet in any orientation,
      // so index BOTH ends and extend the line in both directions
      var key = function (p) { return Math.round(p[0] * 10) + ',' + Math.round(p[1] * 10); };
      var ends = {};
      segs.forEach(function (s, si) {
        [0, 1].forEach(function (end) {
          var k = key(s[end]);
          (ends[k] = ends[k] || []).push({ seg: si, end: end });
        });
      });
      var used = new Array(segs.length);
      var takeNext = function (pt) {
        var cands = (ends[key(pt)] || []).filter(function (c) { return !used[c.seg]; });
        if (!cands.length) return null;
        used[cands[0].seg] = true;
        return segs[cands[0].seg][1 - cands[0].end];
      };
      var lines = [];
      for (var s = 0; s < segs.length; s++) {
        if (used[s]) continue;
        used[s] = true;
        var line = [segs[s][0], segs[s][1]];
        var p;
        while ((p = takeNext(line[line.length - 1]))) line.push(p);
        while ((p = takeNext(line[0]))) line.unshift(p);
        lines.push(line);
      }
      return lines;
    }
    function chaikin(pts) {
      var out = [pts[0]];
      for (var i = 0; i < pts.length - 1; i++) {
        var a = pts[i], b = pts[i + 1];
        out.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25]);
        out.push([a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
      }
      out.push(pts[pts.length - 1]);
      return out;
    }
    var LEVELS = 18, out = [];
    for (var l = 1; l <= LEVELS; l++) {
      var t = mn + (mx - mn) * (l / (LEVELS + 1));
      contoursAt(t).forEach(function (line) {
        if (line.length < 6) return;
        line = chaikin(chaikin(line));
        // keep only lines that touch the disc
        var R2 = 1230 * 1230;
        for (var k = 0; k < line.length; k++) {
          var dx = line[k][0] - 1200, dy = line[k][1] - 1200;
          if (dx * dx + dy * dy < R2) { out.push(line); return; }
        }
      });
    }
    topoLines = out;
    return out;
  }

  // ---------- fonts ----------
  var fontsPromise = null;
  function fontsReady() {
    if (!fontsPromise) {
      fontsPromise = Promise.all([
        document.fonts.load('700 100px "Cinzel Decorative"'),
        document.fonts.load('500 100px Jost'),
        document.fonts.load('400 100px Jost')
      ]).catch(function () { /* draw with fallbacks rather than never drawing */ });
    }
    return fontsPromise;
  }

  // ---------- state ----------
  function currentRole() {
    var v = els.role.value;
    if (v === '__custom') v = (els.customRole.value || '').trim();
    return v.toUpperCase();
  }
  function currentInitial() {
    var v = (els.initial.value || '').trim();
    if (!v) {
      var n = (els.name.value || '').trim();
      v = n ? n.charAt(0) : '';
    }
    return v.replace(/[^\p{L}]/gu, '').toUpperCase().slice(0, 1);
  }
  // optional second initial: never auto-filled, only what the buyer typed
  function currentInitial2() {
    var v = (els.initial2 && els.initial2.value || '').trim();
    return v.replace(/[^\p{L}]/gu, '').toUpperCase().slice(0, 1);
  }
  function designState() {
    return {
      role: currentRole(),
      name: (els.name.value || '').trim(),
      initial: currentInitial(),
      initial2: currentInitial2(),
      date: (els.date.value || '').trim()
    };
  }

  // ---------- drawing ----------
  var COLORS = {
    disc: '#F3EEE4',
    topo: '#C6B89E',
    mono: '#9C6B3F',
    role: '#8A5A33',
    text: '#6E5138'
  };

  // draws text with manual letter-spacing (canvas letterSpacing isn't
  // everywhere yet); returns nothing. x is the CENTER of the run.
  function drawSpaced(ctx, text, cx, baseline, spacing) {
    var widths = [], total = 0, i;
    for (i = 0; i < text.length; i++) {
      var w = ctx.measureText(text[i]).width;
      widths.push(w);
      total += w;
    }
    total += spacing * (text.length - 1);
    var x = cx - total / 2;
    var align = ctx.textAlign;
    ctx.textAlign = 'left';
    for (i = 0; i < text.length; i++) {
      ctx.fillText(text[i], x, baseline);
      x += widths[i] + spacing;
    }
    ctx.textAlign = align;
  }

  // Shrink font size until the run fits maxWidth. Returns the size to use.
  function fitSize(ctx, text, baseSize, weight, family, spacingEm, maxWidth) {
    var size = baseSize;
    for (var guard = 0; guard < 8; guard++) {
      ctx.font = weight + ' ' + size + 'px ' + family;
      var w = 0;
      for (var i = 0; i < text.length; i++) w += ctx.measureText(text[i]).width;
      w += spacingEm * size * (text.length - 1);
      if (w <= maxWidth) break;
      size = Math.floor(size * (maxWidth / w) * 0.98);
    }
    return size;
  }

  // Couple's monogram "J & L": the two initials at half the single-initial
  // size with a smaller ampersand centred on the cap-height band. Laid out by
  // advance width, then the run is shifted so its INK CENTROID sits on cx —
  // advance-width and even bounding-box centring both read as off-centre here
  // (Cinzel's J overhangs its origin on the left, the L's foot swash is a long
  // thin tail on the right), and the centroid is what the owner approved on
  // the first custom order (2026-09-06). The single-initial path below is
  // untouched.
  var PAIR_FONT = '"Cinzel Decorative", Georgia, serif';
  function pairLayout(ctx, a, b, fs) {
    var gap = 0.18 * fs, pen = 0, inkL = Infinity, inkR = -Infinity;
    var prevAlign = ctx.textAlign;
    ctx.textAlign = 'left';
    var items = [a, '&', b].map(function (c) {
      var size = c === '&' ? fs * 0.62 : fs;
      ctx.font = '700 ' + size + 'px ' + PAIR_FONT;
      var mt = ctx.measureText(c);
      var it = { c: c, size: size, x: pen, dy: c === '&' ? -0.35 * (fs - size) : 0 };
      inkL = Math.min(inkL, pen - mt.actualBoundingBoxLeft);
      inkR = Math.max(inkR, pen + mt.actualBoundingBoxRight);
      pen += mt.width + gap;
      return it;
    });
    ctx.textAlign = prevAlign;
    return { items: items, inkL: inkL, inkR: inkR };
  }
  function pairCentroid(lay, fs) {
    // draw the run once to a scratch canvas (ART units) and weigh its alpha
    var pad = 20, w = Math.ceil(lay.inkR - lay.inkL) + pad * 2, h = Math.ceil(fs * 1.6);
    var oc = document.createElement('canvas');
    oc.width = w; oc.height = h;
    var o = oc.getContext('2d');
    o.textAlign = 'left'; o.textBaseline = 'alphabetic'; o.fillStyle = '#000';
    var base = Math.round(fs * 1.15);
    lay.items.forEach(function (it) {
      o.font = '700 ' + it.size + 'px ' + PAIR_FONT;
      o.fillText(it.c, it.x - lay.inkL + pad, base + it.dy);
    });
    var d = o.getImageData(0, 0, w, h).data, sum = 0, sumX = 0;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var al = d[(y * w + x) * 4 + 3];
        if (al) { sum += al; sumX += al * (x + 0.5); }
      }
    }
    return sum ? (sumX / sum) - pad + lay.inkL : (lay.inkL + lay.inkR) / 2;
  }
  function drawPairMonogram(ctx, a, b, fs, cx, baseline) {
    var lay = pairLayout(ctx, a, b, fs);
    var shift = cx - pairCentroid(lay, fs);
    var prevAlign = ctx.textAlign;
    ctx.textAlign = 'left';
    ctx.fillStyle = COLORS.mono;
    lay.items.forEach(function (it) {
      ctx.font = '700 ' + it.size + 'px ' + PAIR_FONT;
      ctx.fillText(it.c, it.x + shift, baseline + it.dy);
    });
    ctx.textAlign = prevAlign;
  }

  // Renders the full design into ctx at `size` px square (white page bg,
  // cream disc, topo lines, text stack). All layout in ART(2400) units.
  function drawDesign(ctx, size, st, pageBg) {
    var s = size / ART;
    ctx.save();
    ctx.scale(s, s);
    // page (JPEG corners outside the disc)
    ctx.fillStyle = pageBg || '#FFFFFF';
    ctx.fillRect(0, 0, ART, ART);
    // disc
    ctx.beginPath();
    ctx.arc(1200, 1200, 1188, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.disc;
    ctx.fill();
    // topo lines, clipped to the disc
    ctx.save();
    ctx.beginPath();
    ctx.arc(1200, 1200, 1176, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = COLORS.topo;
    ctx.lineWidth = 3.2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    var lines = buildTopo();
    for (var li = 0; li < lines.length; li++) {
      var line = lines[li];
      ctx.beginPath();
      ctx.moveTo(line[0][0], line[0][1]);
      for (var pi = 1; pi < line.length; pi++) ctx.lineTo(line[pi][0], line[pi][1]);
      ctx.stroke();
    }
    ctx.restore();

    // ---- text stack ----
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    var MAXW = 1500; // widest run allowed inside the disc
    var rows = [];   // {h: visual height, draw(baseline)}

    if (st.initial && st.initial2) {
      (function () {
        var fs = 450; // half the single-initial size; same row metrics otherwise
        rows.push({
          h: fs * 0.70, gapAfter: 190,
          draw: function (baseline) {
            drawPairMonogram(ctx, st.initial, st.initial2, fs, 1200, baseline);
          }
        });
      })();
    } else if (st.initial) {
      (function () {
        var fs = 900; // Cinzel cap height ~= 0.7em -> ~630px tall monogram
        rows.push({
          h: fs * 0.70, gapAfter: 190,
          draw: function (baseline) {
            ctx.font = '700 ' + fs + 'px "Cinzel Decorative", Georgia, serif';
            ctx.fillStyle = COLORS.mono;
            ctx.fillText(st.initial, 1200, baseline);
          }
        });
      })();
    }
    if (st.role) {
      (function () {
        var fs = fitSize(ctx, st.role, 92, '500', 'Jost, "Century Gothic", sans-serif', 0.34, MAXW);
        rows.push({
          h: fs * 0.72, gapAfter: 96,
          draw: function (baseline) {
            ctx.font = '500 ' + fs + 'px Jost, "Century Gothic", sans-serif';
            ctx.fillStyle = COLORS.role;
            drawSpaced(ctx, st.role, 1200, baseline, 0.34 * fs);
          }
        });
      })();
    }
    if (st.name) {
      (function () {
        var fs = fitSize(ctx, st.name, 96, '400', 'Jost, "Century Gothic", sans-serif', 0.02, MAXW);
        rows.push({
          h: fs * 0.72, gapAfter: 78,
          draw: function (baseline) {
            ctx.font = '400 ' + fs + 'px Jost, "Century Gothic", sans-serif';
            ctx.fillStyle = COLORS.text;
            drawSpaced(ctx, st.name, 1200, baseline, 0.02 * fs);
          }
        });
      })();
    }
    if (st.date) {
      (function () {
        var fs = fitSize(ctx, st.date, 82, '400', 'Jost, "Century Gothic", sans-serif', 0.06, MAXW);
        rows.push({
          h: fs * 0.72, gapAfter: 0,
          draw: function (baseline) {
            ctx.font = '400 ' + fs + 'px Jost, "Century Gothic", sans-serif';
            ctx.fillStyle = COLORS.text;
            drawSpaced(ctx, st.date, 1200, baseline, 0.06 * fs);
          }
        });
      })();
    }

    // centre the whole stack on the disc, biased slightly high like the mockup
    var total = 0, r;
    for (r = 0; r < rows.length; r++) total += rows[r].h + (r < rows.length - 1 ? rows[r].gapAfter : 0);
    var y = 1200 - total / 2 - 30;
    for (r = 0; r < rows.length; r++) {
      y += rows[r].h;
      rows[r].draw(y);
      y += rows[r].gapAfter;
    }

    ctx.restore();
  }

  // ---------- preview ----------
  var previewTimer = null;
  function renderPreview() {
    var cssSize = els.canvas.clientWidth || 320;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var px = Math.round(cssSize * dpr);
    if (els.canvas.width !== px) { els.canvas.width = px; els.canvas.height = px; }
    var ctx = els.canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, px, px);
    drawDesign(ctx, px, designState(), 'rgba(0,0,0,0)');
  }
  function schedulePreview() {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(function () {
      fontsReady().then(renderPreview);
    }, 120);
  }

  // ---------- print file ----------
  var PRINT_SIZE = 2400;          // 8" x 300 DPI, same ceiling as the designer
  var JPEG_QUALITY = 0.92;
  var printTimer = null;
  var printSeq = 0;               // invalidates in-flight renders on change

  function attachDisabled(disabled) {
    // a disabled input is excluded from FormData, so a failed generation
    // still lets the order through with the text properties
    els.file.disabled = disabled;
  }

  function buildPrintFile() {
    var seq = ++printSeq;
    var st = designState();
    if (!st.name && !st.initial) {
      attachDisabled(true);
      return Promise.resolve(false);
    }
    return fontsReady().then(function () {
      if (seq !== printSeq) return false;
      var c = document.createElement('canvas');
      c.width = PRINT_SIZE;
      c.height = PRINT_SIZE;
      drawDesign(c.getContext('2d'), PRINT_SIZE, st, '#FFFFFF');
      return new Promise(function (resolve) {
        c.toBlob(function (blob) {
          if (!blob || seq !== printSeq) { resolve(false); return; }
          try {
            var slug = (st.role + '-' + st.name).toLowerCase()
              .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'design';
            var dt = new DataTransfer();
            dt.items.add(new File([blob], 'monogram-' + slug + '.jpg', { type: 'image/jpeg' }));
            els.file.files = dt.files;
            attachDisabled(false);
            resolve(true);
          } catch (err) {
            // DataTransfer unsupported: order proceeds with text props only
            attachDisabled(true);
            resolve(false);
          }
        }, 'image/jpeg', JPEG_QUALITY);
      });
    });
  }

  function schedulePrintFile() {
    attachDisabled(true); // stale until regenerated
    clearTimeout(printTimer);
    printTimer = setTimeout(function () {
      printTimer = null; // submit fast-path reads this as "attached file is fresh"
      buildPrintFile();
    }, 900);
  }

  // ---------- form wiring ----------
  function onFieldChange() {
    if (els.role.value === '__custom') {
      els.customRoleWrap.style.display = '';
      // only the active role field posts a property
      els.customRole.name = 'properties[Title on Disc]';
      els.role.removeAttribute('name');
    } else {
      els.customRoleWrap.style.display = 'none';
      els.role.name = 'properties[Title on Disc]';
      els.customRole.removeAttribute('name');
    }
    // keep the auto-initial visible as a placeholder hint; only post the
    // Initial property when the buyer actually typed one (the design itself
    // always shows the initial, so an empty order property is just noise)
    var st = designState();
    els.initial.placeholder = st.initial ? st.initial + ' (auto)' : 'A';
    if ((els.initial.value || '').trim()) {
      els.initial.name = 'properties[Initial]';
    } else {
      els.initial.removeAttribute('name');
    }
    // same rule for the optional second initial: only a typed value posts
    if ((els.initial2.value || '').trim()) {
      els.initial2.name = 'properties[Second Initial]';
    } else {
      els.initial2.removeAttribute('name');
    }
    schedulePreview();
    schedulePrintFile();
  }

  ['input', 'change'].forEach(function (evt) {
    [els.role, els.customRole, els.name, els.initial, els.initial2, els.date].forEach(function (el) {
      el.addEventListener(evt, onFieldChange);
    });
  });

  // Regenerate synchronously-ish at submit if the debounce hadn't fired yet.
  // Listening on the FORM (not document) runs before cart-drawer.js's
  // document-level submit interceptor; stopPropagation keeps it from seeing
  // this pass, then requestSubmit() re-fires a clean submit.
  var passThrough = false;
  form.addEventListener('submit', function (e) {
    if (passThrough) { passThrough = false; return; }
    if (!els.file.disabled && els.file.files.length && !printTimer) return; // fresh file already attached
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(printTimer);
    printTimer = null;
    if (els.status) els.status.textContent = 'Preparing your print file…';
    buildPrintFile().then(function () {
      if (els.status) els.status.textContent = '';
      passThrough = true;
      form.requestSubmit();
    });
  });

  // ---------- boot ----------
  attachDisabled(true);
  onFieldChange();
  // warm the topo + fonts while the buyer reads the page
  if ('requestIdleCallback' in window) {
    requestIdleCallback(function () { buildTopo(); fontsReady().then(renderPreview); });
  } else {
    setTimeout(function () { buildTopo(); fontsReady().then(renderPreview); }, 400);
  }
  window.addEventListener('resize', schedulePreview);
})();
